import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import appliBg from '../../assets/appliBg.svg';
import { Button } from '../../components/ui/button';
import { useGetGuarantorIndemnityFormQuery, useSubmitGuarantorIndemnityMutation } from '../../store/api/apiSlice';
import { MEANS_OF_IDENTIFICATION_OPTIONS, INDEMNITY_MAX_LENGTHS, RECOMMENDATION_MAX_LENGTH } from '../../types';
import type { IndemnityClause } from '../../types';
import {
    allAccepted,
    fieldForServerMessage,
    initialFormState,
    signatureMatches,
    toRequest,
    validate,
    type IndemnityFieldErrors,
    type IndemnityFormState,
} from './indemnity';

function formatWhen(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** The global filter omits `success: false` — the useful text is always `message`. */
function readableError(err: any, fallback: string): { text: string; list: string[] } {
    const msg = err?.data?.message ?? err?.message;
    if (Array.isArray(msg)) return { text: msg[0] ?? fallback, list: msg };
    return { text: typeof msg === 'string' ? msg : fallback, list: [] };
}

function Shell({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex min-h-dvh justify-center bg-[#f5f5f5] p-3 sm:p-6 md:p-10">
            <div className="relative w-full max-w-[820px] self-start overflow-hidden rounded-2xl bg-white md:rounded-3xl">
                <img
                    src={appliBg}
                    alt=""
                    className="absolute top-0 right-0 hidden h-[260px] w-auto rounded-tr-3xl object-contain lg:block"
                />
                <div
                    className="absolute top-0 right-0 hidden h-[260px] w-[460px] rounded-tr-3xl lg:block"
                    style={{
                        backgroundImage:
                            'linear-gradient(92deg, rgb(255,255,255) 14%, rgba(255,255,255,0.886) 48%, rgba(255,255,255,0) 110%)',
                    }}
                />
                <div className="relative z-10 px-5 py-8 sm:px-8 md:px-12 md:py-12">{children}</div>
            </div>
        </div>
    );
}

function Field({
    label,
    error,
    children,
    hint,
}: {
    label: string;
    error?: string | undefined;
    hint?: string;
    children: React.ReactNode;
}) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-gray-900">{label}</span>
            {children}
            {hint && !error && <span className="text-xs text-gray-500">{hint}</span>}
            {error && (
                <span role="alert" className="text-xs text-red-600">
                    {error}
                </span>
            )}
        </label>
    );
}

const inputClass = (hasError?: boolean) =>
    `w-full rounded-lg border px-3 py-2.5 text-sm text-gray-900 transition-colors focus:outline-none ${
        hasError
            ? 'border-red-300 focus:border-red-500'
            : 'border-gray-200 focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15'
    }`;

/** Clause text is rendered from the API response — never hardcoded. */
function ClauseBlock({
    clause,
    checked,
    onChange,
    error,
}: {
    clause: IndemnityClause;
    checked: boolean;
    onChange: (next: boolean) => void;
    error?: string | undefined;
}) {
    return (
        <section className="rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900">{clause.heading}</h3>
            <div className="mt-2 space-y-2">
                {clause.paragraphs.map((paragraph, i) => (
                    <p key={i} className="text-[13px] leading-relaxed text-gray-600">
                        {paragraph}
                    </p>
                ))}
            </div>
            <label className="mt-3 flex cursor-pointer items-start gap-2.5 border-t border-gray-100 pt-3">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-[#FF6B35]"
                />
                <span className="text-[13px] leading-snug font-medium text-gray-900">
                    I have read and accept the {clause.heading.toLowerCase()} above.
                </span>
            </label>
            {error && (
                <p role="alert" className="mt-1.5 text-xs text-red-600">
                    {error}
                </p>
            )}
        </section>
    );
}

export default function GuarantorRecommendation() {
    const { token = '' } = useParams();
    const { data, isLoading, error: loadError } = useGetGuarantorIndemnityFormQuery(token, { skip: !token });
    const [submit, { isLoading: isSubmitting }] = useSubmitGuarantorIndemnityMutation();

    const [form, setForm] = useState<IndemnityFormState | null>(null);
    const [errors, setErrors] = useState<IndemnityFieldErrors>({});
    const [formError, setFormError] = useState('');
    const [doneAt, setDoneAt] = useState<string | null>(null);

    // Seed once the prefill arrives. Everything stays editable — the guarantor is
    // correcting what was said about them.
    useEffect(() => {
        if (data && !form) setForm(initialFormState(data.prefill));
    }, [data, form]);

    const set = <K extends keyof IndemnityFormState>(key: K, value: IndemnityFormState[K]) => {
        setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
        setErrors((prev) => ({ ...prev, [key]: undefined }));
        setFormError('');
    };

    const canSubmit = useMemo(() => !!form && allAccepted(form) && !isSubmitting, [form, isSubmitting]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!form) return;

        const found = validate(form);
        setErrors(found);
        if (Object.keys(found).length > 0) {
            setFormError('Please correct the highlighted fields.');
            return;
        }

        try {
            const result = await submit({ token, data: toRequest(form) }).unwrap();
            setDoneAt(result.submittedAt);
        } catch (err: any) {
            const { text, list } = readableError(err, "We couldn't submit this form. Please try again.");

            // The form was already completed — most likely a second tab, or a
            // double submit that raced. Switch to the completed state rather than
            // inviting them to try again, which can only fail the same way.
            if (/already (been )?(completed|submitted)/i.test(text)) {
                setDoneAt(data?.submittedAt ?? new Date().toISOString());
                return;
            }

            // Map what we can onto fields; anything unmapped shows at form level.
            const mapped: IndemnityFieldErrors = {};
            for (const message of list.length > 0 ? list : [text]) {
                const field = fieldForServerMessage(message);
                if (field) mapped[field] = message;
            }
            setErrors((prev) => ({ ...prev, ...mapped }));
            setFormError(Object.keys(mapped).length > 0 ? '' : text);
        }
    };

    // ---------- Loading ----------
    if (isLoading) {
        return (
            <Shell>
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />
                </div>
            </Shell>
        );
    }

    // ---------- Dead link ----------
    if (loadError || !data) {
        return (
            <Shell>
                <div className="py-6 text-center">
                    <AlertCircle className="mx-auto h-10 w-10 text-red-500" aria-hidden="true" />
                    <h1 className="mt-3 text-xl font-bold text-gray-900">This link is not valid</h1>
                    <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
                        The link may have expired or been altered. Please use the link exactly as it was sent to you by
                        email or SMS.
                    </p>
                </div>
            </Shell>
        );
    }

    // ---------- Completed ----------
    const completedAt = doneAt ?? (data.alreadySubmitted ? data.submittedAt : null);
    if (completedAt !== null) {
        return (
            <Shell>
                <div className="py-6 text-center">
                    <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" aria-hidden="true" />
                    <h1 className="mt-3 text-xl font-bold text-gray-900">Form completed</h1>
                    <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
                        Thank you. Your guarantor indemnity form for{' '}
                        <span className="font-medium text-gray-900">{data.applicant.fullName}</span> was submitted
                        {formatWhen(completedAt) ? ` on ${formatWhen(completedAt)}` : ''}. There is nothing further to
                        do.
                    </p>
                </div>
            </Shell>
        );
    }

    if (!form) return null;

    const { applicant } = data;
    const signatureTouched = form.signatureName.trim().length > 0;
    const signatureOk = signatureMatches(form.signatureName, form.fullName);

    return (
        <Shell>
            <header>
                <div className="flex items-center gap-2 text-[#FF6B35]">
                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                    <span className="text-xs font-semibold tracking-[0.15em] uppercase">Guarantor indemnity form</span>
                </div>
                <h1 className="mt-2 text-xl font-bold text-gray-900 sm:text-2xl">Stand as a guarantor</h1>

                {/* Section 1 — read-only, rendered as a sentence, never as inputs.
                    `roleAppliedFor` is always null today, so the phrase is omitted
                    rather than guessed at. */}
                <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    You have been named as a guarantor for{' '}
                    <span className="font-semibold text-gray-900">{applicant.fullName}</span>
                    {applicant.roleAppliedFor ? (
                        <>
                            {' '}
                            for the role of{' '}
                            <span className="font-semibold text-gray-900">{applicant.roleAppliedFor}</span>
                        </>
                    ) : null}
                    {applicant.organisation ? (
                        <>
                            {' '}
                            at <span className="font-semibold text-gray-900">{applicant.organisation}</span>
                        </>
                    ) : null}
                    . Please confirm your details, read each clause, and sign below.
                </p>
            </header>

            <form onSubmit={handleSubmit} className="mt-7 space-y-6" noValidate>
                {/* ---------- Section 2: the guarantor's own details ---------- */}
                <section className="space-y-4">
                    <h2 className="text-sm font-semibold tracking-wide text-gray-900 uppercase">Your details</h2>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Full name" error={errors.fullName}>
                            <input
                                type="text"
                                value={form.fullName}
                                onChange={(e) => set('fullName', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.fullName}
                                autoComplete="name"
                                className={inputClass(!!errors.fullName)}
                            />
                        </Field>
                        <Field label="Phone number" error={errors.phoneNumber}>
                            <input
                                type="tel"
                                value={form.phoneNumber}
                                onChange={(e) => set('phoneNumber', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.phoneNumber}
                                autoComplete="tel"
                                className={inputClass(!!errors.phoneNumber)}
                            />
                        </Field>
                        <Field label="Email address" error={errors.email}>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => set('email', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.email}
                                autoComplete="email"
                                className={inputClass(!!errors.email)}
                            />
                        </Field>
                        <Field label="Occupation" error={errors.occupation}>
                            <input
                                type="text"
                                value={form.occupation}
                                onChange={(e) => set('occupation', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.occupation}
                                className={inputClass(!!errors.occupation)}
                            />
                        </Field>
                    </div>

                    <Field label="Residential address" error={errors.residentialAddress}>
                        <input
                            type="text"
                            value={form.residentialAddress}
                            onChange={(e) => set('residentialAddress', e.target.value)}
                            maxLength={INDEMNITY_MAX_LENGTHS.residentialAddress}
                            autoComplete="street-address"
                            className={inputClass(!!errors.residentialAddress)}
                        />
                    </Field>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Field label="Employer / business name" error={errors.employerBusinessName}>
                            <input
                                type="text"
                                value={form.employerBusinessName}
                                onChange={(e) => set('employerBusinessName', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.employerBusinessName}
                                className={inputClass(!!errors.employerBusinessName)}
                            />
                        </Field>
                        <Field label="Office address" error={errors.officeAddress}>
                            <input
                                type="text"
                                value={form.officeAddress}
                                onChange={(e) => set('officeAddress', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.officeAddress}
                                className={inputClass(!!errors.officeAddress)}
                            />
                        </Field>
                        <Field label="Relationship with the applicant" error={errors.relationshipWithApplicant}>
                            <input
                                type="text"
                                value={form.relationshipWithApplicant}
                                onChange={(e) => set('relationshipWithApplicant', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.relationshipWithApplicant}
                                className={inputClass(!!errors.relationshipWithApplicant)}
                            />
                        </Field>
                        <Field
                            label="How long have you known them?"
                            error={errors.periodKnown}
                            hint="For example, 6 years"
                        >
                            <input
                                type="text"
                                value={form.periodKnown}
                                onChange={(e) => set('periodKnown', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.periodKnown}
                                className={inputClass(!!errors.periodKnown)}
                            />
                        </Field>
                        <Field label="Means of identification" error={errors.meansOfIdentification}>
                            <select
                                value={form.meansOfIdentification}
                                onChange={(e) =>
                                    set(
                                        'meansOfIdentification',
                                        e.target.value as IndemnityFormState['meansOfIdentification'],
                                    )
                                }
                                className={inputClass(!!errors.meansOfIdentification)}
                            >
                                <option value="">Select…</option>
                                {MEANS_OF_IDENTIFICATION_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Identification number" error={errors.identificationNumber}>
                            <input
                                type="text"
                                value={form.identificationNumber}
                                onChange={(e) => set('identificationNumber', e.target.value)}
                                maxLength={INDEMNITY_MAX_LENGTHS.identificationNumber}
                                className={inputClass(!!errors.identificationNumber)}
                            />
                        </Field>
                    </div>
                </section>

                {/* ---------- Recommendation ---------- */}
                <section>
                    <Field
                        label="Your recommendation"
                        error={errors.recommendation}
                        hint={`${form.recommendation.trim().length} / ${RECOMMENDATION_MAX_LENGTH} characters`}
                    >
                        <textarea
                            value={form.recommendation}
                            onChange={(e) => set('recommendation', e.target.value)}
                            maxLength={RECOMMENDATION_MAX_LENGTH}
                            rows={5}
                            placeholder="In your own words, why do you recommend this person?"
                            className={inputClass(!!errors.recommendation)}
                        />
                    </Field>
                </section>

                {/* ---------- Clauses: order and text come from the API ----------
                    Three separate checkboxes, stored separately so each can be
                    evidenced on its own. A single "agree to everything" box would
                    not be equivalent. */}
                <section className="space-y-3">
                    {data.clauses.map((clause) => {
                        const key = clause.key;
                        const stateKey: keyof IndemnityFormState | null =
                            key === 'indemnity'
                                ? 'indemnityAccepted'
                                : key === 'verificationConsent'
                                  ? 'verificationConsentAccepted'
                                  : key === 'declaration'
                                    ? 'declarationAccepted'
                                    : null;
                        if (!stateKey) return null;
                        return (
                            <ClauseBlock
                                key={key}
                                clause={clause}
                                checked={form[stateKey] as boolean}
                                onChange={(next) => set(stateKey, next as never)}
                                error={errors[stateKey]}
                            />
                        );
                    })}
                </section>

                {/* ---------- Signature ---------- */}
                <section>
                    <Field
                        label="Sign by typing your full name"
                        error={errors.signatureName}
                        hint="This must match the full name you gave above."
                    >
                        <input
                            type="text"
                            value={form.signatureName}
                            onChange={(e) => set('signatureName', e.target.value)}
                            maxLength={INDEMNITY_MAX_LENGTHS.fullName}
                            placeholder={form.fullName || 'Your full name'}
                            className={inputClass(!!errors.signatureName || (signatureTouched && !signatureOk))}
                        />
                    </Field>
                    {/* Validated as they type: a mismatch discovered on submit is the
                        easiest way to fail an otherwise complete form. */}
                    {signatureTouched && !signatureOk && !errors.signatureName && (
                        <p className="mt-1 text-xs text-amber-600">
                            This doesn't match "{form.fullName || 'your full name'}" yet.
                        </p>
                    )}
                    {signatureTouched && signatureOk && (
                        <p className="mt-1 flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Signature matches
                        </p>
                    )}
                </section>

                {formError && (
                    <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                        {formError}
                    </p>
                )}

                <div className="flex flex-col gap-2">
                    <Button
                        type="submit"
                        disabled={!canSubmit}
                        className="w-full bg-[#FF6B35] py-6 text-base font-semibold text-white hover:bg-[#F05A22] disabled:opacity-50"
                    >
                        {isSubmitting ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Submitting…
                            </span>
                        ) : (
                            'Submit indemnity form'
                        )}
                    </Button>
                    {!allAccepted(form) && (
                        <p className="text-center text-xs text-gray-500">
                            Tick all three acceptances above to enable submission.
                        </p>
                    )}
                </div>
            </form>
        </Shell>
    );
}
